import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { StaticImageData } from "next/image";
import BigDebRedSmiling from "@/app/assets/images/deb/big-deb-head-red-smiling.png";
import BigDebBlueSmiling from "@/app/assets/images/deb/big-deb-head-blue-smiling.png";
import BigDebPinkSmiling from "@/app/assets/images/deb/big-deb-head-pink-smiling.png";

type FaceExpression = "default" | "smile" | "frown" | "shocked";

interface DebVariantDefinition {
  id: string;
  avatar: StaticImageData;
  voiceId: string;
  voiceGuidance?: string;
}


// OpenAI:  Supported voices are alloy, ash, ballad, coral, echo, fable, onyx, nova, sage, shimmer, and verse.
const debVariants: DebVariantDefinition[] = [
  {
    id: "red",
    voiceId: "coral",
    // ElevenLabs voice ID
    // voiceId: "Xb7hH8MSUJpSbSDYk0k2",
    avatar: BigDebRedSmiling,
    voiceGuidance: "Speak in a young, friendly, informal and cheerful, encouraging way; yet with an air of authority.",
  },
  {
    id: "blue",
    voiceId: "onyx",
    // ElevenLabs voice ID
    // voiceId: "IKne3meq5aSn9XLyUdCD",
    avatar: BigDebBlueSmiling,
    voiceGuidance: "Speak with a more mature tone, an air of authority yet at ease and reassuring.",
  },
  {
    id: "scarlett",
    voiceId: "nova",
    // ElevenLabs voice ID
    // voiceId: "NNLkUFtBEEpaA8qYnd6M",
    avatar: BigDebPinkSmiling,
    voiceGuidance: "Speak rapidly, with boundless energy and enthusiasm, bouncing between words.",
  },
];

interface DebContextProps {
  // Mini Deb state
  isVisible: boolean;
  faceExpression: FaceExpression;
  badgeNumber: number | null;
  showDeb: () => void;
  hideDeb: () => void;
  changeFaceExpression: (expression: FaceExpression) => void;
  setBadgeNumber: (number: number | null) => void;

  // Big Deb state
  currentVariant: DebVariantDefinition;
  cycleVariant: () => void;
}

const DebContext = createContext<DebContextProps | undefined>(undefined);

export const DebProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Mini Deb state
  const [isVisible, setIsVisible] = useState(true);
  const [faceExpression, setFaceExpression] = useState<FaceExpression>("default");
  const [badgeNumber, setBadgeNumberState] = useState<number | null>(null);

  // Big Deb state
  const [currentVariantId, setCurrentVariantId] = useState("red");
  const currentVariant = debVariants.find((s) => s.id === currentVariantId) || debVariants[0];

  const cycleVariant = () => {
    const currentIndex = debVariants.findIndex((s) => s.id === currentVariantId);
    const nextIndex = (currentIndex + 1) % debVariants.length;
    setCurrentVariantId(debVariants[nextIndex].id);
  };

  return (
    <DebContext.Provider
      value={{
        isVisible,
        faceExpression,
        badgeNumber,
        showDeb: () => setIsVisible(true),
        hideDeb: () => setIsVisible(false),
        changeFaceExpression: setFaceExpression,
        setBadgeNumber: setBadgeNumberState,
        currentVariant,
        cycleVariant,
      }}
    >
      {children}
    </DebContext.Provider>
  );
};

export const useDeb = () => {
  const context = useContext(DebContext);
  if (!context) {
    throw new Error("useDeb must be used within a DebProvider");
  }
  return context;
};
